<script>
   /****************************************************************/
   /* PlotDoF                                                      */
   /* -----------------------------------------                    */
   /* shows DoF plot for DD-SIMCA model                            */
   /****************************************************************/

   import { Vector } from 'mdatools/arrays';
   import { mrange, max } from 'mdatools/stat';
   import { Segments, Legend, Lines } from 'svelte-plots-basic/2d';
   import { csvRow, downloadCSV } from '../utils.js';

   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';
    import { pcamodel_get } from '@mdatools-apps/wasm/pca.js';

   let {
      params,                        // JSON with main plot parameters
      props,                         // JSON with main properties (models, results, datasets, etc. formed in AppPlots)
   } = $props()

   // constants
   const xLabel = 'Components';
   const yLabel = 'Degrees of freedom, Nu';
   const title = 'DoF';
   const fileName='plot-dof';

   // locally defined colors
   const LINE_COLORS = {'Nh': '#2679B2', 'Nq': '#D22C2F'};
   const FACE_COLORS = {'Nh': '#f0f8ff', 'Nq': '#fff8f0'};

   // get the properties needed for the plot
   let simca = $derived(props && props.simca ? props.simca : null);
   let model = $derived(props && props.model ? props.model : null);
   let ncomp = $derived(simca ? simca.ncomp : null);

   // get plot parameters (results to show and legend position)
   let dfIDs = $derived(params.dfID !== 'both' ? [params.dfID] : ['Nh', 'Nq']);
   let legendPosition = $derived(params.legendPosition ? params.legendPosition : 'topleft');


   // create legend items based on resIDs array
   let legendItems = $derived(dfIDs ? dfIDs.map(v => ({
      id: v,
      label: v,
      point: {
         marker: 1,
         lineColor: LINE_COLORS[v],
         faceColor: 'white',
         markerSize: 0.85,
         lineWidth: 1.5
      },
      line: {
         lineColor: LINE_COLORS[v],
         lineWidth: 1.5
      }
   })) : null);

   // compute x-coordinates for each result object
   let xValues = $derived(model ? Vector.seq(1, model.ncomp) : null);

   // compute y-coordinates for each result object
   let yValues = $derived(simca && dfIDs ? dfIDs.reduce((acc, id) => {
      const lt = simca.limType === 'classic' ? 'c' : 'r';
      acc[id] = new Vector(pcamodel_get(model, lt + id));
      return acc;
   }, {}) : null);

   // compute axis limits
   let limX = $derived(model ? [0.5, model.ncomp + 0.5] : [0, 1]);

   let limY = $derived.by(() => {
      const ids = Object.keys(yValues);
      if (ids.length < 1) return [0, 1];
      const v = ids.map(id => max(yValues[id]));
      return [0, max(v) * 1.2];
   });


   // method for creating CSV version of the plot data
   function downloadCSVCallback() {

      // 'props.data' is a map keyed by result id: the first set's separator
      const { sep, dec } = Object.values(props.data)[0];

      let x = [];
      let y = [];
      let s = [];

      const n = xValues.v.length;
      const xv = Array.from(xValues.v);
      for (let id of dfIDs) {
         x = [...x, ...xv];
         y = [...y, ...Array.from(yValues[id].v)];
         s = [...s, ...Array(n).fill(id)];
      }

      let out = csvRow(['Set', xLabel, 'Nu'], sep, dec);
      for (let i = 0; i < x.length; i++) {
         out += csvRow([s[i], x[i], y[i]], sep, dec);
      }

      downloadCSV(out, 'plot-dof');
   }
</script>

{#if xValues && yValues }

<Axes {title} {xLabel} {yLabel} {limX} {limY} {fileName} {downloadCSVCallback} whole={true}>

   <!-- vertical line with selected component -->
   {#if ncomp}
   <Segments xStart={[ncomp]} xEnd={[ncomp]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor='#a0a0a0' />
   {/if}

   <!-- data points with statistics -->
   {#each dfIDs as id, i}
      <PlotSeries
         {xValues}
         yValues={yValues[id]}
         plotType='b'
         showLabels={false}
         faceColor={FACE_COLORS[id]}
         mainColor={LINE_COLORS[id]}
         lineWidth={1.5}
      />
   {/each}

   <!-- points for selected component -->
   {#if ncomp}
   {#each dfIDs as id}
      <PlotSeries
         xValues={[xValues.v[ncomp - 1]]}
         yValues={[yValues[id].v[ncomp - 1]]}
         plotType='p'
         showLabels={false}
         faceColor={LINE_COLORS[id]}
         mainColor="#a0a0a0"
         markerSize={1.2}
         lineWidth={1}
      />
   {/each}
   {/if}

   {#if legendItems && legendItems.length > 0}
   <Legend items={legendItems} position={legendPosition} />
   {/if}
</Axes>
{/if}
